/**
 * Seeded SYNTH composer (audition study; the game still plays place/score.ts).
 *
 * Same request shape as the chamber composer: a world seed + biome + scene +
 * night always yields the same piece. The SCENE picks the genre (large city =
 * synthwave, small city = chill synth, exploration = drumless ambient, flight =
 * space drive); the BIOME picks the mode and tone colour; NIGHT slows, darkens
 * and thins it. Everything random comes from one seeded stream per layer.
 */
import { sha256 } from '../place/seed';
import type { MusicBiome } from '../place/score';

export const SYNTH_COMPOSER_VERSION = 'synth-study-1';
export type SynthScene = 'large-city' | 'small-city' | 'exploration' | 'flight' | 'fight';
export interface SynthRequest { seed: string; biome: MusicBiome; scene: SynthScene; night: boolean }

export type InstId = 'pad' | 'stab' | 'drone' | 'bass' | 'arp' | 'lead' | 'bell' | 'keys' | 'kick' | 'snare' | 'clap' | 'hat' | 'ohat' | 'shaker' | 'tom' | 'riser' | 'crash';
export interface SNote {
  inst: InstId; pitch: number; beat: number; beats: number; vel: number; pan?: number; glide?: boolean;
  /** Filter brightness multiplier at the attack (and at the end, for sweeps across a held note). */
  bright?: number; brightEnd?: number;
  /** Echo throw: this note is also sent hard into the delay. */
  throw?: boolean;
  /** Pitch fall/rise (semitones) over the second half of the note. */
  bend?: number;
}

export type Wave = 'sawtooth' | 'square' | 'triangle' | 'sine' | 'pulse';
export interface Patch {
  wave: Wave; voices: number; detune: number; cutoff: number; resonance: number;
  attack: number; decay: number; sustain: number; release: number;
  envAmount: number; sub: number; vibrato: number; glide: number;
  ratio: number; index: number; send: { reverb: number; delay: number }; chorus: boolean; duck: boolean; drive: number;
  describe: string;
  /** Slow filter LFO depth (0 = none). */
  lfo?: number;
  /** Pulse duty cycle for 'pulse' waves. */
  duty?: number;
}
export interface FxParams { reverbSeconds: number; reverbTone: number; delayBeats: number; delayFeedback: number; drive: number; master: number; tone: number; duckDepth: number;
  /** Stereo width, 0 (mono) to 1 (default). */
  width?: number }
export interface Section { label: string; bar: number; bars: number; layers: InstId[] }
export interface SynthScore {
  version: string; request: SynthRequest;
  style: string; title: string; description: string;
  bpm: number; key: number; keyName: string; mode: string; swing: number;
  bars: number; beats: number; duration: number;
  /** All beat positions and BPM use quarter notes, including compound meters. */
  meter?: { numerator: number; denominator: number; grouping: readonly number[] };
  genre?: string;
  progression: string[]; sections: Section[];
  patches: Partial<Record<InstId, Patch>>;
  notes: SNote[]; fx: FxParams;
  /** Composer generation (4 = planned motif development). */
  composer?: 1 | 2 | 3 | 4 | 5;
  /** Energy per bar (0-1), the song's tension arc. */
  energy?: number[];
  /** Master tone-filter automation points. */
  automation?: { beat: number; tone: number }[];
  /** Stop-time breaks: the rhythm section drops out for `beats` (never a full-mix cut). */
  breaks?: { beat: number; beats: number }[];
  /** Per-instrument mix offsets (dB) applied on top of calibration. */
  mix?: Partial<Record<InstId, number>>;
}

// ── seeded streams ──────────────────────────────────────────────────────────
export class Rng {
  private s: number;
  constructor(seed: string, layer: string) { this.s = parseInt(sha256(`${SYNTH_COMPOSER_VERSION}|${seed}|${layer}`).slice(0, 8), 16) >>> 0; }
  next(): number { // mulberry32
    let t = (this.s = (this.s + 0x6d2b79f5) >>> 0);
    t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  range(a: number, b: number): number { return a + (b - a) * this.next(); }
  int(a: number, b: number): number { return Math.floor(this.range(a, b + 1)); }
  pick<T>(items: readonly T[]): T { return items[Math.floor(this.next() * items.length)]; }
  chance(p: number): boolean { return this.next() < p; }
}

// ── theory ──────────────────────────────────────────────────────────────────
export const MODES: Record<string, number[]> = {
  ionian: [0, 2, 4, 5, 7, 9, 11], dorian: [0, 2, 3, 5, 7, 9, 10], phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11], mixolydian: [0, 2, 4, 5, 7, 9, 10], aeolian: [0, 2, 3, 5, 7, 8, 10],
  'phrygian dominant': [0, 1, 4, 5, 7, 8, 10], 'harmonic minor': [0, 2, 3, 5, 7, 8, 11],
};
// Degree loops (0 = tonic) that suit each mode's colour; diminished chords avoided.
export const PROGRESSIONS: Record<string, number[][]> = {
  aeolian: [[0, 5, 2, 6], [0, 6, 5, 6], [5, 6, 0, 0], [0, 3, 5, 4], [0, 5, 3, 6]],
  dorian: [[0, 3, 0, 3], [0, 3, 6, 3], [0, 6, 3, 0], [0, 2, 3, 6]],
  lydian: [[0, 1, 0, 1], [0, 1, 4, 1], [0, 1, 5, 4]],
  mixolydian: [[0, 6, 3, 0], [0, 3, 6, 3], [0, 6, 4, 3]],
  phrygian: [[0, 1, 0, 1], [0, 1, 2, 1], [0, 6, 5, 1]],
  'phrygian dominant': [[0, 1, 0, 1], [0, 1, 6, 1], [0, 5, 1, 0]],
  ionian: [[0, 4, 5, 3], [0, 5, 3, 4]],
  // i-iv-V-i, i-VI-V, i-VI-iv-V, VI-iv-V-i: the major V pulls hard back home.
  'harmonic minor': [[0, 3, 4, 0], [0, 5, 4, 4], [0, 5, 3, 4], [5, 3, 4, 0]],
};
export const KEYS = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];
const ROMAN = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii'];
const mod = (a: number, n: number) => ((a % n) + n) % n;
export class Scale {
  constructor(readonly key: number, readonly steps: number[]) {}
  /** MIDI pitch of a scale degree (any integer; 7 = octave up) around a base octave. */
  pitch(degree: number, octave = 4): number { return 12 * (octave + 1) + this.key + this.steps[mod(degree, 7)] + 12 * Math.floor(degree / 7); }
  chord(degree: number, size: number): number[] { return Array.from({ length: size }, (_, i) => degree + 2 * i); }
  quality(degree: number): 'maj' | 'min' | 'dim' {
    const r = this.steps[mod(degree, 7)], third = mod(this.steps[mod(degree + 2, 7)] - r, 12), fifth = mod(this.steps[mod(degree + 4, 7)] - r, 12);
    return fifth === 6 ? 'dim' : third === 4 ? 'maj' : 'min';
  }
  name(degree: number): string {
    const root = KEYS[mod(this.key + this.steps[mod(degree, 7)], 12)], q = this.quality(degree);
    return root + (q === 'min' ? 'm' : q === 'dim' ? '°' : '');
  }
  roman(degree: number): string { const q = this.quality(degree), r = ROMAN[mod(degree, 7)]; return q === 'maj' ? r.toUpperCase() : r + (q === 'dim' ? '°' : ''); }
}

// ── styles ──────────────────────────────────────────────────────────────────
export interface Archetype {
  style: string; tempo: [number, number]; drums: 'drive' | 'four' | 'soft' | 'none';
  bass: 'octaves' | 'pulse16' | 'walk' | 'sustain'; arp: '16th' | '8th' | 'none'; chordBars: 1 | 2;
  form: { label: string; bars: number; layers: InstId[] }[]; blurb: string;
}
export const ARCHETYPES: Record<SynthScene, Archetype> = {
  'large-city': {
    style: 'Synthwave', tempo: [100, 116], drums: 'drive', bass: 'octaves', arp: '16th', chordBars: 1,
    blurb: 'Neon synthwave: pumping octave bass, 16th arpeggios, gated drums and a singing lead.',
    form: [
      { label: 'intro', bars: 4, layers: ['pad', 'arp', 'riser'] },
      { label: 'groove', bars: 8, layers: ['pad', 'arp', 'bass', 'kick', 'snare', 'hat'] },
      { label: 'theme', bars: 8, layers: ['pad', 'arp', 'bass', 'lead', 'kick', 'snare', 'hat', 'ohat'] },
      { label: 'breakdown', bars: 4, layers: ['pad', 'bell', 'riser'] },
      { label: 'theme II', bars: 8, layers: ['pad', 'arp', 'bass', 'lead', 'kick', 'clap', 'snare', 'hat', 'ohat', 'tom'] },
      { label: 'outro', bars: 4, layers: ['pad', 'arp', 'bell'] },
    ],
  },
  'small-city': {
    style: 'Chillsynth', tempo: [82, 96], drums: 'soft', bass: 'walk', arp: '8th', chordBars: 2,
    blurb: 'Downtempo chill synth: electric-piano chords, a round walking bass, soft swung drums.',
    form: [
      { label: 'intro', bars: 4, layers: ['keys', 'pad'] },
      { label: 'verse', bars: 8, layers: ['keys', 'pad', 'bass', 'kick', 'snare', 'shaker'] },
      { label: 'melody', bars: 8, layers: ['keys', 'pad', 'bass', 'lead', 'kick', 'snare', 'shaker'] },
      { label: 'bells', bars: 8, layers: ['pad', 'bell', 'bass', 'arp', 'kick', 'shaker'] },
      { label: 'melody II', bars: 8, layers: ['keys', 'pad', 'bass', 'lead', 'bell', 'kick', 'snare', 'shaker'] },
      { label: 'outro', bars: 4, layers: ['keys', 'pad'] },
    ],
  },
  exploration: {
    style: 'Ambient', tempo: [64, 76], drums: 'none', bass: 'sustain', arp: '8th', chordBars: 2,
    blurb: 'Drumless ambient space: slow pads over a drone, FM bells in echo, a sparse distant melody.',
    form: [
      { label: 'horizon', bars: 8, layers: ['drone', 'pad'] },
      { label: 'signals', bars: 8, layers: ['drone', 'pad', 'bell'] },
      { label: 'song', bars: 8, layers: ['drone', 'pad', 'lead', 'bell'] },
      { label: 'current', bars: 8, layers: ['drone', 'pad', 'arp', 'bass'] },
      { label: 'return', bars: 8, layers: ['drone', 'pad', 'bell', 'riser'] },
    ],
  },
  fight: {
    style: 'Battle', tempo: [146, 164], drums: 'drive', bass: 'pulse16', arp: '16th', chordBars: 1,
    blurb: 'Combat: fast, dark and aggressive — chugging pulse chords, a rolling distorted 16th bass, double-time drums and a pulse lead that snarls.',
    form: [
      { label: 'standoff', bars: 4, layers: ['pad', 'bass', 'riser'] },
      { label: 'clash', bars: 8, layers: ['stab', 'bass', 'arp', 'lead', 'kick', 'clap', 'snare', 'hat'] },
      { label: 'onslaught', bars: 8, layers: ['stab', 'bass', 'arp', 'lead', 'kick', 'clap', 'snare', 'hat', 'ohat', 'tom'] },
      { label: 'breath', bars: 4, layers: ['pad', 'bass', 'riser'] },
      { label: 'last stand', bars: 8, layers: ['stab', 'bass', 'arp', 'lead', 'kick', 'clap', 'snare', 'hat', 'ohat', 'tom', 'crash'] },
    ],
  },
  flight: {
    style: 'Space drive', tempo: [118, 130], drums: 'four', bass: 'pulse16', arp: '16th', chordBars: 1,
    blurb: 'Driving space synth: rolling 16th bass, four-on-the-floor, wide pads and a soaring lead.',
    form: [
      { label: 'launch', bars: 4, layers: ['pad', 'bass', 'riser'] },
      { label: 'cruise', bars: 8, layers: ['pad', 'bass', 'arp', 'kick', 'hat'] },
      { label: 'theme', bars: 8, layers: ['pad', 'bass', 'arp', 'lead', 'kick', 'clap', 'hat', 'ohat'] },
      { label: 'nebula', bars: 4, layers: ['pad', 'bell', 'riser'] },
      { label: 'theme II', bars: 8, layers: ['pad', 'bass', 'arp', 'lead', 'kick', 'clap', 'hat', 'ohat', 'tom'] },
      { label: 'arrival', bars: 4, layers: ['pad', 'arp'] },
    ],
  },
};
export interface Palette {
  modes: string[]; words: string[]; bright: number; drive: number; reverb: number; delayFb: number;
  pad: Wave[]; lead: Wave[]; arp: Wave[]; bellRatio: [number, number]; tonal: string;
  /** Pulse-wave duty cycle: narrower = thinner, more nasal/hollow. */
  duty: number;
}
export const PALETTES: Record<MusicBiome, Palette> = {
  terran: { modes: ['aeolian', 'dorian', 'mixolydian'], words: ['Verdant', 'Meridian', 'Harbor', 'Canopy', 'Sunline'], bright: 1, drive: .25, reverb: 3.2, delayFb: .38, pad: ['sawtooth'], lead: ['square', 'pulse'], arp: ['square', 'pulse'], duty: 0.25, bellRatio: [2, 3.5], tonal: 'warm analog' },
  ocean: { modes: ['dorian', 'lydian', 'mixolydian'], words: ['Tidal', 'Coral', 'Undertow', 'Lagoon', 'Brine'], bright: .95, drive: .15, reverb: 4.2, delayFb: .52, pad: ['sawtooth', 'triangle'], lead: ['pulse', 'square'], arp: ['pulse', 'square'], duty: 0.3, bellRatio: [1.41, 3], tonal: 'chorused, echoing' },
  ice: { modes: ['lydian', 'aeolian'], words: ['Glacier', 'Crystal', 'Hoarfrost', 'Aurora', 'Silence'], bright: 1.35, drive: .08, reverb: 5.5, delayFb: .45, pad: ['triangle', 'sawtooth'], lead: ['pulse'], arp: ['pulse', 'square'], duty: 0.125, bellRatio: [2, 3.5], tonal: 'glassy' },
  desert: { modes: ['phrygian dominant', 'dorian'], words: ['Mirage', 'Dune', 'Sirocco', 'Oasis', 'Sunspire'], bright: 1.1, drive: .3, reverb: 2.4, delayFb: .35, pad: ['square', 'sawtooth'], lead: ['pulse', 'square'], arp: ['pulse'], duty: 0.2, bellRatio: [2, 3], tonal: 'dry pulse' },
  volcanic: { modes: ['phrygian', 'aeolian'], words: ['Magma', 'Cinder', 'Obsidian', 'Caldera', 'Ember'], bright: .75, drive: .6, reverb: 2.8, delayFb: .32, pad: ['sawtooth'], lead: ['square'], arp: ['square', 'pulse'], duty: 0.35, bellRatio: [1.41, 2.76], tonal: 'dark, distorted' },
  barren: { modes: ['aeolian', 'phrygian'], words: ['Dead Signal', 'Regolith', 'Hollow', 'Static', 'Wane'], bright: .7, drive: .12, reverb: 6, delayFb: .5, pad: ['triangle', 'sine'], lead: ['pulse'], arp: ['pulse'], duty: 0.125, bellRatio: [2, 3], tonal: 'bare, droning' },
};
export const NOUNS: Record<SynthScene, string[]> = {
  'large-city': ['Boulevard', 'Skyline', 'Arcade', 'Nightdrive', 'Overpass'],
  'small-city': ['Terrace', 'Rooftops', 'Lantern', 'Porchlight', 'Tram'],
  exploration: ['Drift', 'Horizon', 'Expanse', 'Stillness', 'Wanderer'],
  flight: ['Vector', 'Orbit', 'Burn', 'Slipstream', 'Parallax'],
  fight: ['Onslaught', 'Clash', 'Reckoning', 'Warpath', 'Fury'],
};

// ── patches ─────────────────────────────────────────────────────────────────
export function makePatches(r: Rng, a: Archetype, p: Palette, night: boolean): Partial<Record<InstId, Patch>> {
  const dark = night ? .6 : 1, b = p.bright * dark;
  const base = (x: Partial<Patch>): Patch => ({
    wave: 'sawtooth', voices: 1, detune: 0, cutoff: 2000, resonance: .7, attack: .005, decay: .2, sustain: .7, release: .2,
    envAmount: 0, sub: 0, vibrato: 0, glide: 0, ratio: 1, index: 0, send: { reverb: .2, delay: 0 }, chorus: false, duck: false, drive: 0, describe: '', ...x,
  });
  const padWave = r.pick(p.pad), padVoices = r.int(3, 4), padDetune = r.range(7, 18);
  const leadWave = r.pick(p.lead), arpWave = r.pick(p.arp);
  const out: Partial<Record<InstId, Patch>> = {
    pad: base({ wave: padWave, voices: padVoices, detune: padDetune, cutoff: r.range(900, 2400) * b, resonance: r.range(.5, 2), attack: a.drums === 'none' ? r.range(1.8, 3.2) : r.range(.35, 1.1), release: r.range(1.4, 2.8), sustain: 1, envAmount: r.range(.3, .8), send: { reverb: .55, delay: .08 }, chorus: true, duck: a.drums !== 'none',
      describe: `${padVoices}× ${padWave}, ${padDetune.toFixed(0)}¢ detune, chorus` }),
    drone: base({ wave: r.pick(['sawtooth', 'triangle'] as Wave[]), voices: 3, detune: r.range(4, 9), cutoff: r.range(350, 800) * b, attack: 4, release: 4, sustain: 1, send: { reverb: .6, delay: 0 },
      describe: 'detuned low drone, slow filter drift' }),
    // Bass: thick, gritty sawtooth or triangle driven into saturation.
    bass: base({ wave: r.pick(a.bass === 'sustain' ? ['triangle'] as Wave[] : ['sawtooth', 'triangle'] as Wave[]), sub: r.range(.4, .8), cutoff: r.range(260, 700) * (night ? .8 : 1), resonance: r.range(2, 7), decay: r.range(.12, .3), sustain: .55, release: .06, envAmount: r.range(1.5, 4), send: { reverb: .04, delay: 0 }, duck: false, drive: Math.min(.3, Math.max(.2, p.drive)),
      describe: 'gritty mono bass (saturated) + sine sub, resonant filter blip' }),
    arp: base({ wave: arpWave, duty: p.duty, cutoff: r.range(900, 2000) * b, resonance: r.range(3, 9), decay: r.range(.12, .26), sustain: 0, release: .08, envAmount: r.range(3, 8), send: { reverb: .3, delay: .38 }, duck: a.drums !== 'none',
      describe: `${arpWave === 'pulse' ? `${Math.round(p.duty * 100)}% pulse` : 'square'} pluck, bright filter blip, echo` }),
    lead: base({ wave: leadWave, duty: p.duty, voices: 2, detune: r.range(3, 9), cutoff: r.range(1600, 3800) * b, resonance: r.range(.7, 3), attack: r.range(.01, .06), release: r.range(.2, .5), sustain: .85, vibrato: r.range(.004, .009), glide: r.range(.04, .09), send: { reverb: .4, delay: .3 },
      describe: `${leadWave === 'pulse' ? `${Math.round(p.duty * 100)}% pulse` : 'square'} lead, buzzy and hollow, delayed vibrato, portamento` }),
    // Soft FM bell: near-harmonic ratio, modest index (high ratios/index read as shrill).
    bell: base({ wave: 'sine', ratio: r.range(...p.bellRatio), index: r.range(1, 2.2), decay: r.range(1.6, 3.2), send: { reverb: .6, delay: .5 },
      describe: 'FM bell' }),
    keys: base({ wave: 'sine', ratio: 1, index: r.range(1, 2.4), decay: r.range(1.2, 2.2), release: .35, send: { reverb: .3, delay: .12 }, duck: false,
      describe: 'FM electric piano with tine' }),
  };
  const drive = a.drums === 'drive' || a.drums === 'four';
  for (const d of ['kick', 'snare', 'clap', 'hat', 'ohat', 'shaker', 'tom', 'riser'] as InstId[]) {
    out[d] = base({ send: { reverb: d === 'snare' || d === 'clap' ? (drive ? .35 : .2) : d === 'riser' ? .5 : d === 'tom' ? .25 : .05, delay: 0 }, drive: d === 'kick' ? p.drive * .5 : 0,
      describe: d === 'kick' ? (drive ? 'punchy 909-style kick' : 'soft round kick') : d === 'snare' ? (drive ? 'gated noise snare' : 'brushed noise snare') : d === 'riser' ? 'filtered noise riser' : `synth ${d}` });
  }
  out.bell!.describe = `FM bell (ratio ${out.bell!.ratio.toFixed(2)})`;
  return out;
}

// ── composition ─────────────────────────────────────────────────────────────
export function composeSynth(req: SynthRequest): SynthScore {
  const r = (layer: string) => new Rng(req.seed, `${req.biome}|${req.scene}|${req.night ? 'n' : 'd'}|${layer}`);
  const world = new Rng(req.seed, 'world'); // shared across scenes: same planet, same key
  const a = ARCHETYPES[req.scene], p = PALETTES[req.biome];
  const key = world.int(0, 11), mode = new Rng(req.seed, `mode|${req.biome}`).pick(p.modes);
  const scale = new Scale(key, MODES[mode]);
  const choose = r('form');
  let bpm = Math.round(choose.range(...a.tempo) * (req.night ? .88 : 1));
  const progression = choose.pick(PROGRESSIONS[mode] ?? PROGRESSIONS.aeolian);
  const swing = a.drums === 'soft' ? choose.range(.08, .16) : 0;
  const patches = makePatches(r('patches'), a, p, req.night);
  const words = r('title');
  const title = `${words.pick(p.words)} ${words.pick(NOUNS[req.scene])}`;

  // Night thins the arrangement: drop open hats/toms, halve-time snare later.
  const sections: Section[] = [];
  let bar = 0;
  for (const s of a.form) {
    const layers = s.layers.filter(l => !(req.night && (l === 'ohat' || l === 'tom' || (l === 'hat' && a.drums === 'drive'))));
    sections.push({ label: s.label, bar, bars: s.bars, layers });
    bar += s.bars;
  }
  const bars = bar, beats = bars * 4;
  const notes: SNote[] = [];
  const add = (n: SNote) => { if (n.beat < beats) notes.push(n); };
  const chordAt = (barIndex: number) => progression[Math.floor(barIndex / a.chordBars) % progression.length];
  const has = (s: Section, l: InstId) => s.layers.includes(l);
  const size = a.style === 'Synthwave' || a.style === 'Space drive' ? 3 : 4; // 7ths for chill/ambient colour

  // Voice-led pad chords.
  let voicing: number[] = [];
  const voice = (degree: number): number[] => {
    const pcs = scale.chord(degree, size).map(d => scale.pitch(d, 4));
    if (!voicing.length) return (voicing = pcs.map(x => x < 57 ? x + 12 : x));
    voicing = pcs.map((x, i) => {
      const target = voicing[i] ?? 62; let best = x;
      for (let o = -24; o <= 24; o += 12) if (Math.abs(x + o - target) < Math.abs(best - target)) best = x + o;
      return Math.max(52, Math.min(76, best));
    }).sort((u, v) => u - v);
    return voicing;
  };

  // Lead motif: a 2-bar phrase of scale steps, answered and varied.
  const mel = r('melody');
  const rhythms = [[0, 3, 6, 8, 10, 14, 16, 19, 22, 24], [0, 2, 4, 8, 12, 16, 18, 20, 24, 28], [0, 4, 6, 10, 16, 20, 22, 26], [2, 4, 8, 11, 14, 18, 20, 24, 27]];
  const rhythm = mel.pick(rhythms);
  const contour: number[] = []; let step = mel.pick([0, 2, 4]);
  for (let i = 0; i < rhythm.length; i++) { contour.push(step); step += mel.pick([-2, -1, -1, 1, 1, 2, 3, -3, 0]); step = Math.max(-2, Math.min(9, step)); }
  const phraseB = contour.map((d, i) => d + (i >= contour.length - 3 ? mel.pick([-1, 1, 2]) : 0));

  const drumsRng = r('drums'), bellRng = r('bells'), humanRng = r('human');
  let riserDone = new Set<number>();
  for (const s of sections) {
    for (let b = s.bar; b < s.bar + s.bars; b++) {
      const t0 = b * 4, degree = chordAt(b), chordStart = b % a.chordBars === 0;
      const chord = scale.chord(degree, size);
      const barInSection = b - s.bar, lastBar = barInSection === s.bars - 1;
      if (chordStart && has(s, 'pad')) {
        for (const pitch of voice(degree)) add({ inst: 'pad', pitch, beat: t0, beats: 4 * a.chordBars, vel: .7 });
      }
      if (has(s, 'drone') && barInSection % 8 === 0) {
        for (const pitch of [scale.pitch(0, 2), scale.pitch(4, 2)]) add({ inst: 'drone', pitch, beat: t0, beats: 32, vel: .8 });
      }
      if (has(s, 'keys')) {
        const pcs = voice(degree).map(x => x - 12 >= 50 ? x - 12 : x);
        const stabs = req.night ? [0] : [0, 1.5, 2.5];
        for (const at of chordStart || a.chordBars === 1 ? stabs : [0.5, 2.5]) for (const pitch of pcs) add({ inst: 'keys', pitch, beat: t0 + at, beats: req.night ? 3.5 : 1.2, vel: .55 + humanRng.range(-.06, .06) });
      }
      if (has(s, 'bass')) {
        const root = scale.pitch(degree, 2), fifth = scale.pitch(degree + 4, 2);
        const bRoot = root > 45 ? root - 12 : root;
        if (a.bass === 'octaves') for (let i = 0; i < 8; i++) add({ inst: 'bass', pitch: bRoot + (i % 2 ? 12 : 0), beat: t0 + i * .5, beats: .42, vel: i % 2 ? .75 : .95 });
        else if (a.bass === 'pulse16') for (let i = 0; i < 16; i++) add({ inst: 'bass', pitch: bRoot + (i % 8 === 7 ? 12 : 0), beat: t0 + i * .25, beats: .2, vel: i % 4 === 0 ? 1 : .72 });
        else if (a.bass === 'walk') {
          const line = [[0, bRoot, 1.4], [1.5, fifth > bRoot + 12 ? fifth - 12 : fifth, .45], [2, bRoot + 12, .9], [3.5, scale.pitch(degree + 6, 2) - 12 >= 28 ? scale.pitch(degree + 6, 2) - 12 : scale.pitch(degree + 6, 2), .45]] as const;
          for (const [at, pitch, len] of line) add({ inst: 'bass', pitch, beat: t0 + at, beats: len, vel: .85 });
        } else if (chordStart) add({ inst: 'bass', pitch: bRoot, beat: t0, beats: 4 * a.chordBars, vel: .8 });
      }
      if (has(s, 'arp') && a.arp !== 'none') {
        const pool = [...chord, ...chord.map(d => d + 7)].map(d => scale.pitch(d, a.style === 'Ambient' ? 5 : 4));
        const pattern = (b + s.bar) % 2 ? [0, 1, 2, 3, 4, 3, 2, 1] : [0, 2, 1, 3, 2, 4, 3, 5];
        const rate = a.arp === '16th' ? .25 : .5, n = 4 / rate;
        for (let i = 0; i < n; i++) {
          if (a.style === 'Ambient' && i % 2 && drumsRng.chance(.5)) continue;
          add({ inst: 'arp', pitch: pool[pattern[i % pattern.length] % pool.length], beat: t0 + i * rate, beats: rate * .7, vel: (i % 4 === 0 ? .9 : .65) + humanRng.range(-.05, .05), pan: i % 2 ? .28 : -.28 });
        }
      }
      if (has(s, 'lead') && barInSection % 2 === 0) {
        const phrase = (barInSection / 2) % 2 ? phraseB : contour, lift = s.label.includes('II') ? 2 : 0;
        const chordTones = scale.chord(degree, 3);
        rhythm.forEach((st, i) => {
          if (a.style === 'Ambient' && i % 2) return;
          let d = phrase[i] + lift + degree;
          if (st % 8 === 0) d = chordTones.reduce((best, c) => { const opts = [c, c + 7, c - 7]; for (const o of opts) if (Math.abs(o - d) < Math.abs(best - d)) best = o; return best; }, chordTones[0]);
          const next = rhythm[i + 1] ?? 32, len = Math.min(3, (next - st) / 4 * .9);
          add({ inst: 'lead', pitch: scale.pitch(d, 5), beat: t0 + st / 4, beats: Math.max(.2, len), vel: .8 + humanRng.range(-.08, .05), glide: i > 0 && mel.chance(.3) });
        });
      }
      if (has(s, 'bell')) {
        const count = a.style === 'Ambient' ? bellRng.int(1, 3) : bellRng.int(1, 2);
        for (let k = 0; k < count; k++) add({ inst: 'bell', pitch: Math.min(84, scale.pitch(bellRng.pick(chord) + bellRng.pick([0, 7]), 4)), beat: t0 + bellRng.int(0, 7) * .5, beats: 2, vel: .5 + bellRng.range(0, .3), pan: bellRng.range(-.6, .6) });
      }
      // Drums
      const halftime = req.night;
      if (has(s, 'kick')) {
        const steps = a.drums === 'four' ? [0, 4, 8, 12] : a.drums === 'soft' ? [0, 7, 10] : halftime ? [0, 10] : [0, 8, 11];
        for (const st of steps) add({ inst: 'kick', pitch: 36, beat: t0 + st / 4, beats: .5, vel: 1 });
      }
      if (has(s, 'snare')) for (const st of halftime ? [8] : [4, 12]) add({ inst: 'snare', pitch: 38, beat: t0 + st / 4, beats: .3, vel: a.drums === 'soft' ? .6 : .9 });
      if (has(s, 'clap')) for (const st of halftime ? [8] : [4, 12]) add({ inst: 'clap', pitch: 39, beat: t0 + st / 4 + .01, beats: .3, vel: .7 });
      if (has(s, 'hat')) for (let st = 0; st < 16; st += a.drums === 'four' ? 1 : 2) add({ inst: 'hat', pitch: 42, beat: t0 + st / 4, beats: .1, vel: st % 4 === 2 ? .8 : .45 + drumsRng.range(0, .15) });
      if (has(s, 'ohat')) add({ inst: 'ohat', pitch: 46, beat: t0 + (a.drums === 'four' ? 2.5 : 3.5), beats: .5, vel: .55 });
      if (has(s, 'shaker')) for (let st = 0; st < 16; st++) add({ inst: 'shaker', pitch: 70, beat: t0 + st / 4 + (st % 2 ? swing * .25 : 0), beats: .1, vel: (st % 2 ? .35 : .55) + drumsRng.range(0, .1) });
      if (has(s, 'tom') && lastBar) for (let i = 0; i < 4; i++) add({ inst: 'tom', pitch: 50 - i * 3, beat: t0 + 3 + i * .25, beats: .3, vel: .7 + i * .06 });
      if (has(s, 'riser') && lastBar && !riserDone.has(s.bar)) { riserDone.add(s.bar); add({ inst: 'riser', pitch: 60, beat: t0 - 4 >= 0 ? t0 - 4 : t0, beats: t0 - 4 >= 0 ? 8 : 4, vel: .7 }); }
    }
  }
  notes.sort((x, y) => x.beat - y.beat);
  const fxr = r('fx');
  const fx: FxParams = {
    reverbSeconds: p.reverb * (req.night ? 1.25 : 1) * (a.style === 'Ambient' ? 1.3 : 1), reverbTone: fxr.range(3500, 7000) * p.bright,
    delayBeats: fxr.pick([.75, .75, .5, 1.5]), delayFeedback: p.delayFb, drive: p.drive, master: .5,
    tone: req.night ? 5200 : 16000, duckDepth: a.drums === 'none' ? 0 : a.drums === 'soft' ? .25 : .5,
  };
  const modeName = mode[0].toUpperCase() + mode.slice(1);
  return {
    version: SYNTH_COMPOSER_VERSION, request: req, style: a.style + (req.night ? ' (night)' : ''), title,
    description: `${a.blurb} ${p.tonal[0].toUpperCase() + p.tonal.slice(1)} ${req.biome} palette in ${KEYS[key]} ${modeName}.${req.night ? ' Night: slower, darker filters, thinner drums, longer reverb.' : ''}`,
    bpm, key, keyName: KEYS[key], mode: modeName, swing, bars, beats, duration: beats * 60 / bpm,
    progression: progression.map(d => `${scale.name(d)} (${scale.roman(d)})`), sections, patches, notes, fx,
  };
}
